import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, Loader2, Bed, Sparkles, Upload, FileText, Image as ImageIcon, Star, CheckCheck, Heart, Mail, Megaphone } from 'lucide-react';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import HotelMapUrlField from '@/components/HotelMapUrlField';
import VendorDotsOfConnection from '@/components/VendorDotsOfConnection';
import DocumentGallery from '@/components/DocumentGallery';
import PinButton from '@/components/PinButton';
import InitiativePicker from '@/components/InitiativePicker';
import { inheritCityPin } from '@/lib/inheritCityPin';
import { useOperatorName, pollenSignature } from '@/hooks/useOperatorName';
import VendorOutreachComposer from '@/components/VendorOutreachComposer';
import VendorOutreachHistory from '@/components/VendorOutreachHistory';
import BrochureManager from '@/components/BrochureManager';

// Hotel-only extras — accommodation type, Recommended/Used tags, rating,
// services, policies. Lives inside the Basic Info tab to avoid yet another
// nav element on what is already a deep page.
const HOTEL_SERVICES = ['Spa', 'Indoor games', 'Outdoor landscape', 'Bar', 'Restaurant'];
const HOTEL_POLICIES = ['No kitchen', 'Only veg', 'Outside food not allowed', 'Alcohol not allowed', 'Pet friendly'];

const Chip = ({ active, onClick, children, testid, tint }) => (
  <button type="button" onClick={onClick}
    data-testid={testid}
    className={`px-2.5 h-7 rounded-full text-[11px] font-medium border transition-colors ${
      active
        ? (tint || 'bg-amber-100 text-amber-900 border-amber-300')
        : 'bg-white text-muted-foreground border-border hover:bg-muted/40'
    }`}>
    {children}
  </button>
);

const HotelExtrasPanel = ({ vendor, updateField }) => {
  const tags = vendor.tags || [];
  const services = vendor.services || [];
  const policies = vendor.policies || [];
  const rating = vendor.rating;
  const used = tags.includes('Used');
  const toggleIn = (arr, key, item) => {
    const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
    updateField(key, next);
  };
  return (
    <div className="sm:col-span-2 space-y-3 pt-3 border-t">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Accommodation Type *</Label>
          <Select value={vendor.accommodation_type || ''} onValueChange={(v) => updateField('accommodation_type', v)}>
            <SelectTrigger className="h-9" data-testid="vendor-accommodation-type"><SelectValue placeholder="Hotel / Resort / Villa / Boutique / Homestays / Luxury Chain" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Hotel">Hotel</SelectItem>
              <SelectItem value="Resort">Resort</SelectItem>
              <SelectItem value="Villa">Villa</SelectItem>
              <SelectItem value="Boutique">Boutique</SelectItem>
              <SelectItem value="Homestays">Homestays</SelectItem>
              <SelectItem value="Luxury Chain">Luxury Chain</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="flex items-center gap-2">
            Rating
            {used && (rating == null || Number(rating) <= 0) && (
              <span className="text-[10px] text-amber-700">required for Used hotels</span>
            )}
          </Label>
          <div className="flex items-center gap-1.5" data-testid="vendor-rating">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                type="button"
                key={n}
                onClick={() => updateField('rating', Number(rating) === n ? null : n)}
                className="p-0.5"
                aria-label={`Rate ${n} stars`}
                data-testid={`vendor-rating-${n}`}
              >
                <Star className={`w-5 h-5 ${rating != null && n <= Number(rating) ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'}`} />
              </button>
            ))}
            <span className="text-xs text-muted-foreground ml-1 tabular-nums">{rating ? `${rating}/5` : '—'}</span>
          </div>
        </div>
      </div>

      <div>
        <Label className="flex items-center gap-2">
          Tags
          <span className="text-[10px] text-muted-foreground">(Used is auto-applied when this hotel appears in a tour)</span>
        </Label>
        <div className="flex flex-wrap gap-2 mt-1">
          <Chip
            active={tags.includes('Recommended')}
            tint="bg-emerald-100 text-emerald-900 border-emerald-300"
            onClick={() => toggleIn(tags, 'tags', 'Recommended')}
            testid="vendor-tag-Recommended"
          ><Heart className="w-3 h-3 inline mr-1" />Recommended</Chip>
          <Chip
            active={used}
            tint="bg-sky-100 text-sky-900 border-sky-300"
            onClick={() => toggleIn(tags, 'tags', 'Used')}
            testid="vendor-tag-Used"
          ><CheckCheck className="w-3 h-3 inline mr-1" />Used</Chip>
        </div>
      </div>

      <div>
        <Label>Services</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {HOTEL_SERVICES.map(s => (
            <Chip key={s} active={services.includes(s)}
              onClick={() => toggleIn(services, 'services', s)}
              testid={`vendor-service-${s.replace(/\s+/g, '-')}`}>{s}</Chip>
          ))}
        </div>
      </div>

      <div>
        <Label>Policies</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {HOTEL_POLICIES.map(p => (
            <Chip key={p} active={policies.includes(p)}
              tint="bg-rose-100 text-rose-900 border-rose-300"
              onClick={() => toggleIn(policies, 'policies', p)}
              testid={`vendor-policy-${p.replace(/\s+/g, '-')}`}>{p}</Chip>
          ))}
        </div>
      </div>
    </div>
  );
};

const PollenDotsVendorDetail = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [extractText, setExtractText] = useState('');
  const [extracting, setExtracting] = useState(false);
  // When the hotel has multiple phones we open a bottom-sheet to let the
  // user pick which number receives the WhatsApp message (front-desk vs
  // reservations vs owner).
  const [waPickerOpen, setWaPickerOpen] = useState(false);
  const [waPickerCtx, setWaPickerCtx] = useState({ phones: [], text: '' });
  const [outreachRefresh, setOutreachRefresh] = useState(0);
  // Resolve the assigned operator name for the logged-in shared inbox
  // (e.g. team@pollendots.com → "Rishita Dey"). Falls back to the Google
  // display name when no team_members entry exists.
  const operatorName = useOperatorName(user?.email, user?.name);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchVendor = useCallback(async () => {
    if (isNew) {
      const params = new URLSearchParams(window.location.search);
      const initialType = params.get('type') || 'Hotel';
      setVendor({
        name: '', type: initialType, contact_name: '', phone: '', email: '',
        address: '', location: '', website: '', check_in_time: '', check_out_time: '',
        cancellation_policy: '', bank_account_name: '', bank_account_number: '',
        bank_name: '', bank_ifsc: '', gst_number: '', notes: '', rooms: [], amenities: [],
        initiatives: [],
      });
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(`${POLLEN_API}/vendors/${id}`);
      if (res.ok) {
        const d = await res.json();
        d.rooms = d.rooms || [];
        d.amenities = d.amenities || [];
        d.initiatives = d.initiatives || [];
        setVendor(d);
      } else { toast.error('Vendor not found'); navigate('/pollen-dots/vendors'); }
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [id, isNew]);

  const [pinnedLoc, setPinnedLoc] = useState(null);  // existing exact pin for this hotel/activity

  useEffect(() => { fetchVendor(); }, [fetchVendor]);

  // Look up the existing pin for this vendor (Hotel / Activity / Guide /
  // Driver) in the shared pollen_locations DB so we can prefill the map
  // URL field. Agent-type vendors don't have a location pin counterpart.
  useEffect(() => {
    if (!vendor || !vendor.name) { setPinnedLoc(null); return; }
    if (!['Hotel', 'Activity', 'Guide', 'Driver'].includes(vendor.type)) {
      setPinnedLoc(null); return;
    }
    const type = vendor.type.toLowerCase();
    const name = vendor.name.trim();
    (async () => {
      try {
        const res = await authFetch(`${POLLEN_API}/locations?type=${type}&q=${encodeURIComponent(name)}`);
        if (!res.ok) return;
        const list = await res.json();
        const match = (list || []).find(l => (l.name || '').toLowerCase() === name.toLowerCase());
        if (match) setPinnedLoc(match); else setPinnedLoc(null);
      } catch { /* ignore */ }
    })();
  }, [vendor?.name, vendor?.type]);  // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (key, val) => setVendor(v => ({ ...v, [key]: val }));
  const addRoom = () => setVendor(v => ({ ...v, rooms: [...(v.rooms || []), { category: '', max_occupancy: '', room_count: '', ep_price: 0, cp_price: 0, map_price: 0, ap_price: 0, extra_adult_price: 0, extra_child_price: 0, notes: '' }] }));
  const updateRoom = (i, k, val) => setVendor(v => ({ ...v, rooms: v.rooms.map((r, idx) => idx === i ? { ...r, [k]: val } : r) }));
  const removeRoom = (i) => setVendor(v => ({ ...v, rooms: v.rooms.filter((_, idx) => idx !== i) }));
  const addSuggestedRoom = (sr) => setVendor(v => ({ ...v, rooms: [...(v.rooms || []), { room_count: 0, ...sr }] }));

  const addAmenity = () => setVendor(v => ({ ...v, amenities: [...(v.amenities || []), { name: '', category: 'Experience', price: 0, unit: 'per person', description: '', is_bookable: true }] }));
  const updateAmenity = (i, k, val) => setVendor(v => ({ ...v, amenities: v.amenities.map((a, idx) => idx === i ? { ...a, [k]: val } : a) }));
  const removeAmenity = (i) => setVendor(v => ({ ...v, amenities: v.amenities.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!vendor.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: vendor.name, type: vendor.type, contact_name: vendor.contact_name || '',
        phone: vendor.phone || '', email: vendor.email || '',
        address: vendor.address || '', location: vendor.location || '', website: vendor.website || '',
        check_in_time: vendor.check_in_time || '', check_out_time: vendor.check_out_time || '',
        cancellation_policy: vendor.cancellation_policy || '',
        bank_account_name: vendor.bank_account_name || '',
        bank_account_number: vendor.bank_account_number || '',
        bank_name: vendor.bank_name || '',
        bank_ifsc: vendor.bank_ifsc || '',
        gst_number: vendor.gst_number || '',
        notes: vendor.notes || '',
        initiatives: vendor.initiatives || [],
        // Hotel extras (Feb 2026) — accommodation type + tags + services +
        // policies + rating. Sent unconditionally; backend ignores them for
        // non-Hotel vendor types.
        accommodation_type: vendor.accommodation_type || '',
        tags: vendor.tags || [],
        services: vendor.services || [],
        policies: vendor.policies || [],
        rating: vendor.rating == null ? null : Number(vendor.rating),
        rooms: (vendor.rooms || []).map(r => ({
          category: r.category || '',
          max_occupancy: r.max_occupancy || '',
          room_count: parseInt(r.room_count, 10) || 0,
          meal_plan: r.meal_plan || '',
          price: parseFloat(r.price) || 0,
          ep_price: parseFloat(r.ep_price) || 0,
          cp_price: parseFloat(r.cp_price) || 0,
          map_price: parseFloat(r.map_price) || 0,
          ap_price: parseFloat(r.ap_price) || 0,
          extra_adult_price: parseFloat(r.extra_adult_price) || 0,
          extra_child_price: parseFloat(r.extra_child_price) || 0,
          notes: r.notes || '',
        })),
        amenities: (vendor.amenities || []).map(a => ({
          name: a.name || '',
          category: a.category || 'Experience',
          price: parseFloat(a.price) || 0,
          unit: a.unit || '',
          description: a.description || '',
          is_bookable: a.is_bookable !== false,
        })),
      };
      if (isNew) {
        const res = await authFetch(`${POLLEN_API}/vendors`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (res.ok) { const d = await res.json(); toast.success('Vendor created'); navigate(`/pollen-dots/vendor/${d.id}`); }
        else { toast.error('Failed to create'); }
      } else {
        const res = await authFetch(`${POLLEN_API}/vendors/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (res.ok) { toast.success('Saved'); fetchVendor(); }
        else { toast.error('Failed to save'); }
      }
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const mergeExtracted = (d) => {
    setVendor(v => ({
      ...v,
      phone: d.phone || v.phone,
      email: d.email || v.email,
      contact_name: d.contact_name || v.contact_name,
      address: d.address || v.address,
      website: d.website || v.website,
      check_in_time: d.check_in_time || v.check_in_time,
      check_out_time: d.check_out_time || v.check_out_time,
      cancellation_policy: d.cancellation_policy || v.cancellation_policy,
      bank_account_name: d.bank_account_name || v.bank_account_name,
      bank_account_number: d.bank_account_number || v.bank_account_number,
      bank_name: d.bank_name || v.bank_name,
      bank_ifsc: d.bank_ifsc || v.bank_ifsc,
      gst_number: d.gst_number || v.gst_number,
      notes: d.notes ? (v.notes ? `${v.notes}\n${d.notes}` : d.notes) : v.notes,
      rooms: [...(v.rooms || []), ...((d.rooms || []).filter(r => r.category))],
    }));
    toast.success('Extracted — review & save');
    setExtractOpen(false);
    setExtractText('');
  };

  const handleExtractText = async () => {
    if (!extractText.trim()) return;
    setExtracting(true);
    try {
      const res = await authFetch(`${POLLEN_API}/vendors/extract-text`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: extractText }) });
      if (res.ok) { mergeExtracted(await res.json()); } else { toast.error('Extraction failed'); }
    } catch { toast.error('Error'); }
    setExtracting(false);
  };

  const handleExtractFile = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch(`${POLLEN_API}/vendors/extract-file`, { method: 'POST', body: fd });
      if (res.ok) { mergeExtracted(await res.json()); } else { const e = await res.json(); toast.error(e.detail || 'Extraction failed'); }
    } catch { toast.error('Error'); }
    setExtracting(false);
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div></Layout>;
  if (!vendor) return null;

  const isHotel = vendor.type === 'Hotel';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-4 min-w-0 pb-24 lg:pb-24" data-testid="vendor-detail">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/pollen-dots/vendors')} className="shrink-0 -ml-2"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex-1 min-w-0">
              {isNew ? (
                <div className="flex items-center gap-2">
                  <Input value={vendor.name} onChange={e => updateField('name', e.target.value)} placeholder="Vendor name" className="h-9 max-w-sm" data-testid="new-vendor-name" />
                  <select value={vendor.type} onChange={e => updateField('type', e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="new-vendor-type">
                    {['Hotel', 'Transport', 'Driver', 'Activity', 'Guide', 'Agent'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              ) : (
                <h1 className="text-base sm:text-2xl font-bold leading-tight flex items-center gap-2 min-w-0">
                  <span className="truncate" data-testid="vendor-name-heading">{vendor.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] sm:text-xs">{vendor.type}</Badge>
                </h1>
              )}
            </div>
          </div>
          {/* Action strip — horizontally scrollable on mobile, inline on sm+ */}
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-none">
            <div className="flex items-center gap-2 sm:justify-end sm:flex-wrap whitespace-nowrap">
            {!isNew && <PinButton entityType="vendor" entityId={vendor.id} />}
            {isHotel && !isNew && ((vendor.email || '').trim() || (vendor.phone || '').trim() || true) && (() => {
              // Split comma-separated emails / phones so we can address
              // the first one and bcc the rest. Some homestays only
              // operate over WhatsApp, so we also offer a WA share with
              // the same templated message body.
              const emails = (vendor.email || '').split(/[,;]/).map(s => s.trim()).filter(Boolean);
              const phones = (vendor.phone || '').split(/[,;]/).map(s => s.trim()).filter(Boolean);
              const subject = `B2B / Partner rates request — Pollen Dots × ${vendor.name || ''}`;
              const body =
`Hello Team,

Greetings from Pollen Dots.

We're a curated travel platform working with vetted properties across India and we'd love to onboard ${vendor.name || 'your property'} for our upcoming season.

Could you please share your latest B2B / Partner / Corporate tariff sheet covering:
  • Room types & meal-plan rates (EP / CP / MAP / AP)
  • Off-season & peak-season variants
  • Group rates (4+ rooms)
  • Cancellation policy
  • Inclusions / add-ons (transfers, extra bed, etc.)

If you have a TAC/contract template, please attach the same.

Looking forward to a long partnership.

${pollenSignature(operatorName)}
`;
              const ccList = ['team@pollendots.com', ...emails.slice(1)].join(',');
              const mailto = emails.length
                ? `mailto:${encodeURIComponent(emails[0])}?cc=${encodeURIComponent(ccList)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
                : null;
              // For WhatsApp we send a slightly tighter message (no
              // 6-bullet block since wa.me text-only formatting can't
              // bold/list reliably). Pick the first phone; strip non-digits.
              const waMsg = `Hello, Greetings from Pollen Dots.\n\nWe're a curated travel platform working with vetted properties across India and we'd love to onboard *${vendor.name || 'your property'}* for our upcoming season.\n\nCould you please share your B2B / Partner / Corporate tariff sheet covering room types, meal-plan rates (EP/CP/MAP/AP), seasonal variants, group rates (4+ rooms), and cancellation policy?\n\nLooking forward to a long partnership.\n\n${pollenSignature(operatorName)}`;
              const phoneDigits = phones.length ? phones[0].replace(/\D+/g, '') : '';
              const waUrl = phoneDigits
                ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waMsg)}`
                : `https://wa.me/?text=${encodeURIComponent(waMsg)}`;
              return (
                <>
                  {mailto ? (
                    <a href={mailto}
                      className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800 text-sm font-medium"
                      data-testid="email-hotel-btn"
                      title={`Email ${emails[0]}${emails.length > 1 ? ` (+${emails.length - 1} cc)` : ''}`}>
                      <Mail className="w-3.5 h-3.5" />Email
                    </a>
                  ) : (
                    // No email on file — still surface the button so it's
                    // visually consistent across hotels. Tap prompts to
                    // add an email instead of silently doing nothing.
                    <button
                      type="button"
                      onClick={() => toast.error('No email on file. Add one in Basic Info to use the templated B2B request.')}
                      className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50/60 text-sky-700/70 text-sm font-medium"
                      data-testid="email-hotel-btn-empty"
                      title="No email on file — tap to learn how to add one">
                      <Mail className="w-3.5 h-3.5" />Email
                    </button>
                  )}
                  {phones.length > 0 && (
                    phones.length === 1 ? (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer"
                        className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium"
                        data-testid="whatsapp-hotel-btn"
                        title={`WhatsApp ${phones[0]}`}>
                        <Megaphone className="w-3.5 h-3.5" />WhatsApp
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setWaPickerCtx({ phones, text: waMsg }); setWaPickerOpen(true); }}
                        className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium"
                        data-testid="whatsapp-hotel-btn"
                        title="Pick which number receives the message">
                        <Megaphone className="w-3.5 h-3.5" />WhatsApp <span className="text-[10px] opacity-70">({phones.length})</span>
                      </button>
                    )
                  )}
                </>
              );
            })()}
            {isHotel && !isNew && <Button variant="outline" size="sm" onClick={() => setExtractOpen(true)} data-testid="ai-extract-btn"><Sparkles className="w-4 h-4 mr-1" />AI Extract</Button>}
            {isHotel && isNew && <Button variant="outline" size="sm" onClick={() => setExtractOpen(true)} data-testid="ai-extract-btn-new"><Sparkles className="w-4 h-4 mr-1" />AI Extract</Button>}
            {isHotel && !isNew && (
              <Button variant="outline" size="sm"
                      onClick={() => setBrochureOpen(true)}
                      className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                      data-testid="vendor-brochure-btn">
                <Sparkles className="w-4 h-4 mr-1" />Brochure
              </Button>
            )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="basic">
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-none">
            <TabsList className="inline-flex whitespace-nowrap">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              {isHotel && <TabsTrigger value="rooms">Rooms & Pricing</TabsTrigger>}
              {isHotel && !isNew && <TabsTrigger value="tariff" data-testid="vendor-tariff-tab">Tariff Sheets</TabsTrigger>}
              {(vendor.type === 'Guide' || vendor.type === 'Driver' || vendor.type === 'Transport') && !isNew && (
                <TabsTrigger value="dots" data-testid="vendor-dots-tab">Dots of Connection</TabsTrigger>
              )}
              <TabsTrigger value="policies">Policies & Hours</TabsTrigger>
              <TabsTrigger value="finance">Bank & Tax</TabsTrigger>
              {!isNew && <TabsTrigger value="outreach" data-testid="vendor-outreach-tab">Outreach</TabsTrigger>}
            </TabsList>
          </div>

          {(vendor.type === 'Guide' || vendor.type === 'Driver' || vendor.type === 'Transport') && !isNew && (
            <TabsContent value="dots">
              <VendorDotsOfConnection vendorId={vendor.id} vendorName={vendor.name} vendorType={vendor.type} />
            </TabsContent>
          )}

          {isHotel && !isNew && (
            <TabsContent value="tariff">
              <Card><CardContent className="pt-4">
                <DocumentGallery
                  scope={{ vendor_id: vendor.id }}
                  category="Tariff"
                  title="Tariff Sheets"
                  emptyHint="Drop in this hotel's tariff PDF, Excel, DOCX or image. Preview inline — no download needed."
                  testId="hotel-tariff-gallery"
                />
              </CardContent></Card>
            </TabsContent>
          )}

          <TabsContent value="basic">
            <Card><CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={vendor.name || ''} onChange={e => updateField('name', e.target.value)} /></div>
              <div><Label>Contact Person</Label><Input value={vendor.contact_name || ''} onChange={e => updateField('contact_name', e.target.value)} /></div>
              <div><Label>Phone <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input value={vendor.phone || ''} onChange={e => updateField('phone', e.target.value)} placeholder="+91 9317752915, +91 9805541209" /></div>
              <div><Label>Email <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input type="text" inputMode="email" value={vendor.email || ''} onChange={e => updateField('email', e.target.value)} placeholder="ops@hotel.com, reservations@hotel.com" /></div>
              <div><Label>Location (city / area)</Label>
                <LocationAutocomplete
                  value={vendor.location || ''}
                  type="city"
                  className="h-10 text-sm"
                  onChange={v => updateField('location', v)}
                  onSelect={async (loc) => {
                    updateField('location', loc.name);
                    const pinnable = ['Hotel', 'Activity', 'Guide', 'Driver'].includes(vendor.type);
                    const alreadyPinned = pinnedLoc?.lat != null && pinnedLoc?.lng != null && pinnedLoc?.verified;
                    if (!pinnable || alreadyPinned) return;
                    if (loc.lat == null || loc.lng == null) {
                      toast.info(`${loc.name} has no pin yet — paste a Google Maps URL below to pin this ${vendor.type.toLowerCase()}.`);
                      return;
                    }
                    // Prefill the URL field instantly so the operator sees
                    // the inherited value before/while the pin is committed.
                    const inheritUrl = loc.google_maps_url || `${loc.lat}, ${loc.lng}`;
                    setPinnedLoc(p => ({ ...(p || {}), google_maps_url: inheritUrl, lat: loc.lat, lng: loc.lng, verified: false }));
                    if (!vendor.name?.trim()) {
                      toast.info(`${loc.name} pin ready — enter the ${vendor.type.toLowerCase()}'s name then tap Verify.`);
                      return;
                    }
                    const r = await inheritCityPin({ cityLoc: loc, entityName: vendor.name, entityType: vendor.type });
                    if (r.already_pinned) {
                      setPinnedLoc({ google_maps_url: r.google_maps_url, lat: r.lat, lng: r.lng, verified: r.verified });
                      toast.info(`${vendor.name} already has its own map pin — kept it.`);
                    } else if (r.inherited) {
                      setPinnedLoc({ google_maps_url: r.google_maps_url, lat: r.lat, lng: r.lng, verified: false });
                      toast.success(`Inherited ${loc.name} city pin (approximate) — paste this ${vendor.type.toLowerCase()}'s own Google Maps URL below and tap Verify for the exact pin.`);
                    }
                  }}
                  placeholder="e.g., Shillong, Paro"
                  testId="vendor-location-input"
                />
              </div>
              {(vendor.type === 'Hotel' || vendor.type === 'Activity') && (
                <div className="sm:col-span-2">
                  <HotelMapUrlField
                    entityName={vendor.name}
                    entityType={vendor.type.toLowerCase()}
                    city={vendor.location}
                    initialUrl={pinnedLoc?.google_maps_url}
                    initialLat={pinnedLoc?.lat}
                    initialLng={pinnedLoc?.lng}
                    initialVerified={pinnedLoc?.verified}
                    onPinned={(p) => setPinnedLoc({ ...pinnedLoc, ...p, verified: true })}
                    label={`Google Maps URL — pins this ${vendor.type.toLowerCase()} on share maps`}
                  />
                </div>
              )}
              {(vendor.type === 'Guide' || vendor.type === 'Driver') && vendor.name && (
                <div className="sm:col-span-2">
                  <HotelMapUrlField
                    entityName={vendor.name}
                    entityType={vendor.type.toLowerCase()}
                    city={vendor.location}
                    initialUrl={pinnedLoc?.google_maps_url}
                    initialLat={pinnedLoc?.lat}
                    initialLng={pinnedLoc?.lng}
                    initialVerified={pinnedLoc?.verified}
                    onPinned={(p) => setPinnedLoc({ ...pinnedLoc, ...p, verified: true })}
                    label={`Google Maps URL — pins this ${vendor.type.toLowerCase()}'s base on share maps`}
                  />
                </div>
              )}
              <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={vendor.address || ''} onChange={e => updateField('address', e.target.value)} /></div>
              <div><Label>Website</Label><Input value={vendor.website || ''} onChange={e => updateField('website', e.target.value)} /></div>
              {/* Guide/Driver — photo + bio appear on the Customer Profile "People" tab */}
              {(vendor.type === 'Guide' || vendor.type === 'Driver') && (
                <>
                  <div><Label>Photo URL</Label>
                    <Input value={vendor.photo_url || ''} onChange={e => updateField('photo_url', e.target.value)}
                      placeholder="https://…/photo.jpg" data-testid="vendor-photo-url" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Square photo works best; shown on customer's "People" tab.</p>
                  </div>
                  <div className="sm:col-span-2"><Label>Short Bio</Label>
                    <Textarea rows={2} value={vendor.bio || ''} onChange={e => updateField('bio', e.target.value)}
                      placeholder={`e.g., "Born and raised in Bhutan, loves sharing local stories on long drives."`}
                      data-testid="vendor-bio" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Optional — 1-2 lines that help customers remember this person.</p>
                  </div>
                </>
              )}
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={3} value={vendor.notes || ''} onChange={e => updateField('notes', e.target.value)} /></div>

              {vendor.type === 'Hotel' && (
                <HotelExtrasPanel vendor={vendor} updateField={updateField} />
              )}

              {['Hotel', 'Activity', 'Guide', 'Driver'].includes(vendor.type) && (
                <div className="sm:col-span-2 pt-2 border-t">
                  <InitiativePicker
                    value={vendor.initiatives || []}
                    onChange={(arr) => updateField('initiatives', arr)}
                    testId="vendor-initiatives"
                  />
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          {isHotel && (
            <TabsContent value="rooms">
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Bed className="w-4 h-4" />Room Categories</CardTitle>
                  <Button size="sm" variant="outline" onClick={addRoom} data-testid="add-room"><Plus className="w-3 h-3 mr-1" />Add Category</Button>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(vendor.suggested_rooms || []).length > 0 && (
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs">
                      <p className="font-medium mb-2 text-blue-900">Auto-detected from past tours — click to add:</p>
                      <div className="flex flex-wrap gap-2">
                        {vendor.suggested_rooms.map((sr, i) => (
                          <Button key={i} size="sm" variant="outline" className="h-7 text-xs" onClick={() => addSuggestedRoom(sr)} data-testid={`suggested-room-${i}`}>
                            + {sr.category}{sr.meal_plan ? ` (${sr.meal_plan})` : ''}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(vendor.rooms || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No room categories yet. Add manually or use AI Extract.</p>
                  ) : <>
                    {(() => {
                      const totalRooms = (vendor.rooms || []).reduce((s, r) => s + (parseInt(r.room_count, 10) || 0), 0);
                      return (
                        <div className="rounded-md border bg-amber-50 border-amber-200 p-2.5 flex items-center justify-between" data-testid="hotel-total-rooms">
                          <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold">Total rooms in hotel</div>
                          <div className="text-lg font-bold text-amber-900 tabular-nums">{totalRooms || '—'}</div>
                        </div>
                      );
                    })()}
                    {(vendor.rooms || []).map((r, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 bg-muted/20">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="sm:col-span-2"><Label className="text-xs">Category *</Label><Input className="h-8 text-sm" value={r.category || ''} onChange={e => updateRoom(i, 'category', e.target.value)} placeholder="e.g. Deluxe, Suite, Premium Valley View" /></div>
                          <div><Label className="text-xs">Max Occupancy</Label><Input className="h-8 text-sm" value={r.max_occupancy || ''} onChange={e => updateRoom(i, 'max_occupancy', e.target.value)} placeholder="e.g. 2A+1C" /></div>
                          <div><Label className="text-xs"># of Rooms</Label><Input type="number" min="0" className="h-8 text-sm" value={r.room_count ?? ''} onChange={e => updateRoom(i, 'room_count', e.target.value)} placeholder="0" data-testid={`room-count-${i}`} /></div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 text-destructive mt-5" onClick={() => removeRoom(i)} data-testid={`remove-room-${i}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>

                      {/* Meal plan price matrix */}
                      <div className="bg-white rounded-md border p-2">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Meal Plan Pricing (₹ per room / night)</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          <MealPlanInput i={i} k="ep_price"  label="EP"  full="Room only"           value={r.ep_price}  onChange={updateRoom} />
                          <MealPlanInput i={i} k="cp_price"  label="CP"  full="Breakfast"           value={r.cp_price}  onChange={updateRoom} />
                          <MealPlanInput i={i} k="map_price" label="MAP" full="Breakfast + 1 meal"  value={r.map_price} onChange={updateRoom} />
                          <MealPlanInput i={i} k="ap_price"  label="AP"  full="All meals"           value={r.ap_price}  onChange={updateRoom} />
                        </div>
                      </div>

                      {/* Extras */}
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Extra Adult (₹)</Label><Input type="number" className="h-8 text-sm" value={r.extra_adult_price ?? ''} onChange={e => updateRoom(i, 'extra_adult_price', e.target.value)} placeholder="0" data-testid={`room-extra-adult-${i}`} /></div>
                        <div><Label className="text-xs">Extra Child (₹)</Label><Input type="number" className="h-8 text-sm" value={r.extra_child_price ?? ''} onChange={e => updateRoom(i, 'extra_child_price', e.target.value)} placeholder="0" data-testid={`room-extra-child-${i}`} /></div>
                      </div>

                      <div><Label className="text-xs">Notes</Label><Input className="h-8 text-sm" value={r.notes || ''} onChange={e => updateRoom(i, 'notes', e.target.value)} placeholder="Inclusions, restrictions, special conditions" /></div>

                      {/* Legacy single price field (only shown if it has a value, for backward compat) */}
                      {(parseFloat(r.price) > 0 && !r.ep_price && !r.cp_price && !r.map_price && !r.ap_price) && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          Legacy single price: ₹{r.price} ({r.meal_plan || 'no meal plan'}). Please move to specific meal-plan columns above.
                        </div>
                      )}
                    </div>
                  ))}
                  </>}
                </CardContent>
              </Card>

              {/* Extras / Amenities */}
              <Card className="mt-4">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-base">Extra Amenities & Bookable Services</CardTitle>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Experiences, spa, dining add-ons, packages — anything customers can book on top of the room.</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={addAmenity} data-testid="add-amenity"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(vendor.amenities || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No amenities yet. Use this to list signature experiences (e.g. sunrise trek), bundled packages (honeymoon, wellness), or on-demand services (spa, laundry, airport pickup).</p>
                  ) : (vendor.amenities || []).map((a, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 bg-muted/10">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-6 gap-2">
                          <div className="sm:col-span-3">
                            <Label className="text-xs">Name *</Label>
                            <Input className="h-8 text-sm" value={a.name || ''} onChange={e => updateAmenity(i, 'name', e.target.value)} placeholder="e.g. Sunrise Trek to Mawlyngbna" data-testid={`amenity-name-${i}`} />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Category</Label>
                            <Select value={a.category || 'Experience'} onValueChange={v => updateAmenity(i, 'category', v)}>
                              <SelectTrigger className="h-8 text-sm" data-testid={`amenity-category-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Experience">Experience</SelectItem>
                                <SelectItem value="Service">Service</SelectItem>
                                <SelectItem value="Package">Package</SelectItem>
                                <SelectItem value="Dining">Dining / F&B</SelectItem>
                                <SelectItem value="Wellness">Wellness / Spa</SelectItem>
                                <SelectItem value="Transport">Transfer</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Bookable?</Label>
                            <Select value={a.is_bookable === false ? 'false' : 'true'} onValueChange={v => updateAmenity(i, 'is_bookable', v === 'true')}>
                              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Yes — priced</SelectItem>
                                <SelectItem value="false">Complimentary</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 text-destructive mt-5" onClick={() => removeAmenity(i)} data-testid={`remove-amenity-${i}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>

                      {a.is_bookable !== false && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Price (₹)</Label>
                            <Input type="number" className="h-8 text-sm" value={a.price ?? ''} onChange={e => updateAmenity(i, 'price', e.target.value)} placeholder="0" data-testid={`amenity-price-${i}`} />
                          </div>
                          <div>
                            <Label className="text-xs">Unit</Label>
                            <Select value={a.unit || 'per person'} onValueChange={v => updateAmenity(i, 'unit', v)}>
                              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per person">per person</SelectItem>
                                <SelectItem value="per room">per room</SelectItem>
                                <SelectItem value="per night">per night</SelectItem>
                                <SelectItem value="per session">per session</SelectItem>
                                <SelectItem value="per day">per day</SelectItem>
                                <SelectItem value="flat">flat / one-time</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input className="h-8 text-sm" value={a.description || ''} onChange={e => updateAmenity(i, 'description', e.target.value)} placeholder="What's included? Any restrictions?" data-testid={`amenity-desc-${i}`} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="policies">
            <Card><CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Check-in Time</Label><Input value={vendor.check_in_time || ''} onChange={e => updateField('check_in_time', e.target.value)} placeholder="14:00" /></div>
              <div><Label>Check-out Time</Label><Input value={vendor.check_out_time || ''} onChange={e => updateField('check_out_time', e.target.value)} placeholder="11:00" /></div>
              <div className="sm:col-span-2"><Label>Cancellation Policy</Label><Textarea rows={4} value={vendor.cancellation_policy || ''} onChange={e => updateField('cancellation_policy', e.target.value)} /></div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="finance">
            <Card><CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Account Holder Name</Label><Input value={vendor.bank_account_name || ''} onChange={e => updateField('bank_account_name', e.target.value)} /></div>
              <div><Label>Bank Name</Label><Input value={vendor.bank_name || ''} onChange={e => updateField('bank_name', e.target.value)} /></div>
              <div><Label>Account Number</Label><Input value={vendor.bank_account_number || ''} onChange={e => updateField('bank_account_number', e.target.value)} /></div>
              <div><Label>IFSC Code</Label><Input value={vendor.bank_ifsc || ''} onChange={e => updateField('bank_ifsc', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>GST Number</Label><Input value={vendor.gst_number || ''} onChange={e => updateField('gst_number', e.target.value)} /></div>
            </CardContent></Card>
          </TabsContent>

          {/* Outreach — composer + history. Available once the vendor exists. */}
          {!isNew && (
            <TabsContent value="outreach" className="space-y-4">
              <VendorOutreachComposer
                vendor={vendor}
                onSent={() => setOutreachRefresh(k => k + 1)}
              />
              <Card><CardContent className="pt-4">
                <h3 className="text-sm font-semibold mb-2">Recent outreach</h3>
                <VendorOutreachHistory vendorId={vendor.id} refreshKey={outreachRefresh} />
              </CardContent></Card>
            </TabsContent>
          )}
        </Tabs>

        {/* AI Extract Dialog */}
        <Dialog open={extractOpen} onOpenChange={setExtractOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" />AI Extract Hotel Details</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Paste hotel rate card text, or upload a PDF / image / screenshot. AI will extract rooms, pricing, policies, bank details.</p>
              <div className="flex gap-2 flex-wrap">
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted" data-testid="upload-image">
                  <ImageIcon className="w-4 h-4" /> Image
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleExtractFile(e.target.files?.[0])} />
                </label>
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted" data-testid="upload-pdf">
                  <FileText className="w-4 h-4" /> PDF / DOC / XLSX
                  <input type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,.txt" className="hidden" onChange={e => handleExtractFile(e.target.files?.[0])} />
                </label>
              </div>
              <div>
                <Label>Or paste text</Label>
                <Textarea rows={8} value={extractText} onChange={e => setExtractText(e.target.value)} placeholder="Paste rate card / email / WhatsApp message here..." data-testid="extract-text" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setExtractOpen(false)}>Close</Button>
                <Button onClick={handleExtractText} disabled={extracting || !extractText.trim()} data-testid="extract-text-btn">
                  {extracting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}Extract from Text
                </Button>
              </div>
              {extracting && <p className="text-xs text-muted-foreground text-center">Extracting... may take 10-20 seconds</p>}
            </div>
          </DialogContent>
        </Dialog>

        {/* WhatsApp number-picker — opens when a Hotel has multiple phone
            numbers so the user can decide which one receives the B2B
            rates request (front-desk vs reservations vs owner). */}
        <Sheet open={waPickerOpen} onOpenChange={setWaPickerOpen}>
          <SheetContent
            side="bottom"
            className="z-[80] max-h-[80vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-6 rounded-t-2xl"
            data-testid="wa-picker-sheet"
          >
            <SheetHeader>
              <SheetTitle className="text-base">Pick a number to WhatsApp</SheetTitle>
              <SheetDescription className="text-[11px]">
                {vendor?.name} has {waPickerCtx.phones.length} numbers on file. Tap the one you want to reach (front-desk, reservations, owner, etc.).
              </SheetDescription>
            </SheetHeader>
            <div className="mt-3 space-y-2">
              {waPickerCtx.phones.map((p, i) => {
                const digits = p.replace(/\D+/g, '');
                const href = digits
                  ? `https://wa.me/${digits}?text=${encodeURIComponent(waPickerCtx.text)}`
                  : `https://wa.me/?text=${encodeURIComponent(waPickerCtx.text)}`;
                return (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setWaPickerOpen(false)}
                    className="flex items-center justify-between rounded-lg border bg-white hover:bg-emerald-50 hover:border-emerald-300 transition-colors p-3"
                    data-testid={`wa-picker-${i}`}
                  >
                    <div>
                      <div className="text-sm font-semibold">{p}</div>
                      <div className="text-[11px] text-muted-foreground">wa.me/{digits || '—'}</div>
                    </div>
                    <Megaphone className="w-4 h-4 text-emerald-700" />
                  </a>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>

        {/* Persistent Save Bar — always visible above the mobile bottom-nav.
            Operators never have to scroll up/down to find Save. */}
        <div
          className="fixed left-0 right-0 z-[55] bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-4 py-2.5 flex items-center gap-2 bottom-16 lg:bottom-0 lg:left-64"
          data-testid="vendor-save-bar"
        >
          <Button
            size="sm"
            className="flex-1 sm:flex-none h-10 text-sm font-medium"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-vendor-sticky"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {isNew ? 'Create' : 'Save Changes'}
          </Button>
          {!isNew && (
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              onClick={() => fetchVendor()}
              disabled={saving}
              data-testid="vendor-discard-changes"
            >
              Discard
            </Button>
          )}
        </div>
      </div>
      {!isNew && isHotel && (
        <BrochureManager
          open={brochureOpen}
          onOpenChange={setBrochureOpen}
          ownerKind="hotel"
          ownerId={vendor.id}
          defaultTitle={vendor.name || ''}
        />
      )}
    </Layout>
  );
};

export default PollenDotsVendorDetail;

// Compact meal-plan price input with abbreviated label + tooltip
function MealPlanInput({ i, k, label, full, value, onChange }) {
  return (
    <div className="group">
      <label className="text-[10px] font-semibold text-muted-foreground flex items-center justify-between" title={full}>
        <span>{label}</span>
        <span className="text-[9px] text-muted-foreground/70 hidden sm:inline">{full}</span>
      </label>
      <input
        type="number"
        value={value ?? ''}
        onChange={e => onChange(i, k, e.target.value)}
        placeholder="0"
        className="mt-0.5 w-full h-8 text-sm px-2 rounded border border-input bg-white focus:outline-none focus:ring-1 focus:ring-primary"
        data-testid={`room-${k}-${i}`}
      />
    </div>
  );
}
